import { useStripePaymentsEnabled } from "../hooks/useStripePaymentsEnabled";
import RetryIcon from "@mui/icons-material/Refresh";
import LoadingButton from "@mui/lab/LoadingButton";
import { IconButton, MenuItem, Select, Tooltip } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useCallback, useState } from "react";
import { useRefresh } from "react-admin";
import { getAuthenticatedBackendClient } from "../api/backend.api.js";
import { grants } from "../backoffice.access_control.js";
import { reportError } from "../backoffice.utils.js";
import { InvoiceStateEnum, type PaymentModeEnum } from "../generated/backendClient";
import { DialogCloseButton } from "../misc/DialogCloseButton.js";
import { MoneyCurrency } from "../misc/Money.js";
import { UserBalanceItem } from "../providers/invoicesProvider.js";
import type { Student } from "../providers/studentsProvider.js";
import { useDialog } from "../hooks/useDialog.js";

type _PaymentMode = PaymentModeEnum | "UNCHANGED";

function isRetryPaymentAllowed(invoiceState: InvoiceStateEnum, invoicePaymentMode?: PaymentModeEnum | null): boolean {
  const alwaysAllowedStates: InvoiceStateEnum[] = ["PAYMENT_REVOKED", "UNPAID", "UNPAID_WITH_PREPAID_CREDITS"];
  if (alwaysAllowedStates.includes(invoiceState)) {
    return true;
  }
  if (invoiceState === "AWAITING_PAYMENT" && invoicePaymentMode === "STRIPE_BANK_TRANSFER") {
    return true;
  }
  return false;
}

export function RetryPaymentButton({
  invoiceState,
  invoiceId,
  paymentAmount,
  description,
  student,
  invoicePaymentMode,
}: {
  invoiceState: InvoiceStateEnum;
  invoiceId: string;
  invoicePaymentMode?: PaymentModeEnum | null;
  student: Student;
  paymentAmount: UserBalanceItem["amount"];
  description: string;
}) {
  const stripePaymentsEnabled = useStripePaymentsEnabled(student.drivingSchoolId);
  const refresh = useRefresh();
  const { openDialog, dialogProps, closeDialog } = useDialog("retryPayments");
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState<_PaymentMode>("UNCHANGED");
  const retryPayment = useCallback(async () => {
    try {
      const backendClient = await getAuthenticatedBackendClient();
      setIsLoading(true);
      await backendClient.invoice.invoiceRetryPaymentCreate({
        id: invoiceId,
        requestBody: paymentMode !== "UNCHANGED" ? { payment_mode: paymentMode } : {},
      });
      setIsLoading(false);
      showSuccessToast(`Die Zahlung wurde wiederholt.`);
      refresh();
    } catch (error) {
      reportError(`Failed to retry payment for invoice ${invoiceId}`, error);
      showErrorToast(`Die Zahlung konnte nicht wiederholt werden.`);
    } finally {
      setIsLoading(false);
      closeDialog();
    }
  }, [invoiceState, invoiceId, paymentMode]);
  const paymentModeOptions: { label: string; paymentMode: _PaymentMode; selected: boolean }[] = [
    {
      label: "Gleiche Zahlungsmethode wie zuvor",
      selected: true,
      paymentMode: "UNCHANGED",
    },
    {
      label: "Nur mit vorausgezahltem Guthaben",
      paymentMode: "PREPAID_CREDITS_ONLY",
      selected: false,
    },
    ...(student.paymentMethod === "card" || student.paymentMethod === "sepa"
      ? [
          {
            label: "Nur mit Karte / Lastschrift",
            paymentMode: "STRIPE_PAYMENT_INTENT" as PaymentModeEnum,
            selected: false,
          },
        ]
      : []),
  ];

  if (!stripePaymentsEnabled || !isRetryPaymentAllowed(invoiceState, invoicePaymentMode)) {
    return null;
  }

  const canSelectPaymentMethod = grants.has("retryPayments");

  return (
    <>
      <Tooltip title="Wiederholen">
        <IconButton onClick={openDialog}>
          <RetryIcon />
        </IconButton>
      </Tooltip>
      <Dialog {...dialogProps}>
        <DialogTitle>Zahlung wiederholen?</DialogTitle>
        <DialogCloseButton onClick={closeDialog} disabled={isLoading} />
        <DialogContent>
          <DialogContentText>
            Die Zahlung in Höhe von{" "}
            <strong>
              <MoneyCurrency {...paymentAmount} />
            </strong>{" "}
            für <em>{description}</em> wiederholen?
          </DialogContentText>
          <DialogContentText>Die Zahlmethode des Fahrschülers wird erneut belastet.</DialogContentText>
        </DialogContent>
        <DialogActions>
          {canSelectPaymentMethod && (
            <Select<_PaymentMode>
              onChange={({ target: { value } }) => setPaymentMode(value as _PaymentMode)}
              value={paymentMode}
              size="small"
              style={{ marginRight: 10 }}
            >
              {paymentModeOptions.map((action) => (
                <MenuItem value={action.paymentMode}>{action.label}</MenuItem>
              ))}
            </Select>
          )}
          <LoadingButton variant="contained" loading={isLoading} onClick={retryPayment} autoFocus>
            Zahlung wiederholen
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
